import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Lock, Eye, EyeOff, Sparkles } from "lucide-react";

/**
 * Household split block — inline attach for record forms (expense, bill,
 * subscription, renewal). Captures:
 *   • ownership (Personal / Partner / Joint / Household / Unassigned)
 *   • visibility (Private / Shared Summary / Shared Full / Selected Members)
 *   • paid_by_user_id
 *   • belongs_to_user_ids (which members the record is "for")
 *   • split[]  — { user_id, pct } rows summing to 100
 *
 * The block silently returns null while the workspace is still Personal
 * so it never intrudes on single-user setups.
 *
 * All fields are stored on the record itself — the backend
 * `PATCH /household/records/{coll}/{rid}/ownership` endpoint handles
 * ownership + visibility, and the split payload is written through the
 * form's own PATCH via the `value` field passed back to the parent.
 */
const OWNERSHIPS   = ["Personal", "Partner", "Joint", "Household", "Unassigned"];
const VISIBILITIES = ["Private", "Shared Summary", "Shared Full", "Selected Members"];

const VIS_ICON = {
    "Private": Lock, "Shared Summary": EyeOff,
    "Shared Full": Eye, "Selected Members": Users,
};


export default function HouseholdSplitBlock({ value = {}, onChange }) {
    const [household, setHousehold] = useState(null);
    const [members, setMembers]     = useState([]);
    const [row, setRow] = useState({
        ownership:  value.ownership  || "Personal",
        visibility: value.visibility || "Shared Summary",
        paid_by_user_id: value.paid_by_user_id || "",
        belongs_to_user_ids: value.belongs_to_user_ids || [],
        split: value.split || [],
        selected_members: value.selected_members || [],
    });

    useEffect(() => {
        api.get("/household").then(r => {
            setHousehold(r.data?.household || null);
            setMembers((r.data?.members || []).filter(m => m.status === "Active"));
        }).catch(() => {});
    }, []);

    const emit = (next) => { setRow(next); onChange?.(next); };
    const set  = (k, v) => emit({ ...row, [k]: v });

    // Split helpers
    const addSplitRow = (uid) => {
        if (!uid) return;
        if (row.split.find(s => s.user_id === uid)) return;
        emit({ ...row, split: [...row.split, { user_id: uid, pct: 0 }] });
    };
    const setSplitPct = (uid, pct) =>
        emit({ ...row, split: row.split.map(s => s.user_id === uid ? { ...s, pct: Number(pct || 0) } : s) });
    const removeSplitRow = (uid) =>
        emit({ ...row, split: row.split.filter(s => s.user_id !== uid) });
    const splitEven = () => {
        if (row.split.length === 0) return;
        const each = Math.floor(100 / row.split.length);
        const remainder = 100 - each * row.split.length;
        emit({ ...row, split: row.split.map((s, i) => ({ ...s, pct: i === 0 ? each + remainder : each })) });
    };
    const splitTotal = row.split.reduce((s, r) => s + Number(r.pct || 0), 0);

    if (!household || household.workspace_type === "Personal") return null;

    const VisIcon = VIS_ICON[row.visibility] || Users;

    return (
        <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-4" data-testid="household-split-block">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
                        <Users size={11} /> Household · Ownership · Split
                    </div>
                    <div className="text-sm text-stone-700 mt-0.5">
                        Paid by · Belongs to · Split %. Organisational only — VAULT never moves money.
                    </div>
                </div>
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 flex items-center gap-1">
                    <VisIcon size={11} /> {row.visibility}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                    <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Ownership</span>
                    <select value={row.ownership} onChange={e => set("ownership", e.target.value)}
                            className="mt-1 w-full text-[13px] border border-stone-300 rounded px-2 py-1.5 bg-white"
                            data-testid="hh-split-ownership">
                        {OWNERSHIPS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Visibility</span>
                    <select value={row.visibility} onChange={e => set("visibility", e.target.value)}
                            className="mt-1 w-full text-[13px] border border-stone-300 rounded px-2 py-1.5 bg-white"
                            data-testid="hh-split-visibility">
                        {VISIBILITIES.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10.5px] uppercase tracking-widest text-stone-500">Paid by</span>
                    <select value={row.paid_by_user_id} onChange={e => set("paid_by_user_id", e.target.value)}
                            className="mt-1 w-full text-[13px] border border-stone-300 rounded px-2 py-1.5 bg-white"
                            data-testid="hh-split-paidby">
                        <option value="">—</option>
                        {members.map(m => <option key={m.id} value={m.user_id}>{m.name || m.email}</option>)}
                    </select>
                </label>
            </div>

            <div className="mt-3">
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 mb-1.5">Belongs to</div>
                <div className="flex flex-wrap gap-1.5">
                    {members.map(m => {
                        const on = row.belongs_to_user_ids.includes(m.user_id);
                        return (
                            <button key={m.id} type="button"
                                    onClick={() => set("belongs_to_user_ids",
                                        on ? row.belongs_to_user_ids.filter(x => x !== m.user_id)
                                           : [...row.belongs_to_user_ids, m.user_id])}
                                    className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full border ${on ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700"}`}
                                    data-testid={`hh-split-belongsto-${m.user_id}`}>
                                {m.name || m.email}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mt-3">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500">Split %</div>
                    <button type="button" onClick={splitEven}
                            className="text-[11px] font-semibold text-amber-800 hover:text-amber-900 inline-flex items-center gap-1"
                            data-testid="hh-split-even">
                        <Sparkles size={10} /> Split evenly
                    </button>
                </div>
                <div className="space-y-1.5">
                    {row.split.map(s => {
                        const m = members.find(x => x.user_id === s.user_id);
                        return (
                            <div key={s.user_id} className="flex items-center gap-2" data-testid={`hh-split-row-${s.user_id}`}>
                                <span className="text-[12px] text-stone-700 flex-1 truncate">{m?.name || m?.email || s.user_id}</span>
                                <input type="number" min="0" max="100" step="1" value={s.pct}
                                       onChange={e => setSplitPct(s.user_id, e.target.value)}
                                       className="w-20 text-[13px] text-right tabular-nums border border-stone-300 rounded px-2 py-1 bg-white" />
                                <span className="text-[11.5px] text-stone-500">%</span>
                                <button type="button" onClick={() => removeSplitRow(s.user_id)}
                                        className="text-[11px] text-rose-700 hover:text-rose-900">Remove</button>
                            </div>
                        );
                    })}
                    <div className="flex items-center gap-2">
                        <select onChange={e => { addSplitRow(e.target.value); e.target.value = ""; }}
                                defaultValue=""
                                className="flex-1 text-[13px] border border-stone-300 rounded px-2 py-1.5 bg-white"
                                data-testid="hh-split-add">
                            <option value="">Add member to split…</option>
                            {members
                                .filter(m => !row.split.find(s => s.user_id === m.user_id))
                                .map(m => <option key={m.id} value={m.user_id}>{m.name || m.email}</option>)}
                        </select>
                        <span className={`text-[11px] font-semibold tabular-nums px-2 py-1 rounded ${splitTotal === 100 ? "text-emerald-700 bg-emerald-50" : "text-amber-800 bg-amber-50"}`}
                              data-testid="hh-split-total">
                            Total {splitTotal}%
                        </span>
                    </div>
                </div>
                {row.split.length > 0 && splitTotal !== 100 && (
                    <div className="text-[11px] text-amber-800 mt-1.5">
                        Split percentages should add up to 100 — they currently total {splitTotal}%.
                    </div>
                )}
            </div>
        </div>
    );
}
